import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, Trash2, Ban, RefreshCw, ChevronLeft, ChevronRight,
  AlertTriangle, CheckCircle, XCircle, Loader2, Filter, MoreVertical,
  Mail, Phone, Calendar, Activity, User, Archive
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FitnessMembers() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [members, setMembers] = useState([]);
  const [archivedIds, setArchivedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedMember, setSelectedMember] = useState(null);
  const [actionModal, setActionModal] = useState({ open: false, type: null, member: null });
  const [actionLoading, setActionLoading] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch the list of ids that are archived locally so we hide them from the live list
  const fetchArchivedIds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/fitness-archive/ids`, { headers: getAuthHeaders() });
      setArchivedIds(res.data?.ids || []);
    } catch (err) {
      console.warn('Failed to load archived ids:', err);
      setArchivedIds([]);
    }
  };

  // Fetch members from API
  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', currentPage);
      params.append('limit', 20);

      const [usersRes, archivedRes] = await Promise.all([
        axios.get(`${API_URL}/api/fitness/users?${params.toString()}`),
        axios.get(`${API_URL}/api/admin/fitness-archive/ids`, { headers: getAuthHeaders() }).catch(() => ({ data: { ids: [] } })),
      ]);

      const archivedSet = new Set(archivedRes.data?.ids || []);
      setArchivedIds(Array.from(archivedSet));

      if (usersRes.data) {
        const rawList = usersRes.data.users || usersRes.data.data || usersRes.data || [];
        // Hide archived rows from the main list
        const filtered = Array.isArray(rawList) ? rawList.filter((m) => !archivedSet.has(m.id)) : [];
        setMembers(filtered);
        setTotalPages(usersRes.data.totalPages || Math.ceil((usersRes.data.total || 0) / 20) || 1);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, currentPage]);

  // Search handler with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') {
        setCurrentPage(1);
        fetchMembers();
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Archive member (only allowed when suspended)
  const handleArchiveMember = async (member) => {
    if (member.status !== 'suspended') {
      toast.error(language === 'ar' ? 'يجب إيقاف العضو أولاً قبل الأرشفة' : 'Suspend the member first');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/fitness-archive/users/${member.id}`,
        { snapshot: { name: member.name, email: member.email, phone: member.phone, role: member.role, status: member.status } },
        { headers: getAuthHeaders() },
      );
      toast.success(language === 'ar' ? 'تمت الأرشفة. يمكنك إيجاد العضو في صفحة الأرشيف.' : 'Archived. Find the member in the Archive page.');
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      setActionModal({ open: false, type: null, member: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الأرشفة' : 'Archive failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Suspend/Unsuspend member
  const handleToggleSuspend = async (memberId, currentStatus) => {
    setActionLoading(true);
    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    try {
      await axios.patch(`${API_URL}/api/fitness/users/${memberId}`, { status: newStatus });
      toast.success(
        newStatus === 'suspended' 
          ? (language === 'ar' ? 'تم إيقاف الحساب' : 'Account suspended')
          : (language === 'ar' ? 'تم تفعيل الحساب' : 'Account activated')
      );
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, status: newStatus } : m));
      setActionModal({ open: false, type: null, member: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في تحديث الحساب' : 'Failed to update account'));
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      member: { ar: 'عضو', en: 'Member', color: 'bg-blue-500/20 text-blue-400' },
      trainer: { ar: 'مدرب', en: 'Trainer', color: 'bg-purple-500/20 text-purple-400' },
      admin: { ar: 'مشرف', en: 'Admin', color: 'bg-amber-500/20 text-amber-400' },
    };
    return roles[role] || roles.member;
  };

  const getStatusLabel = (status) => {
    const statuses = {
      active: { ar: 'نشط', en: 'Active', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
      suspended: { ar: 'موقوف', en: 'Suspended', color: 'bg-red-500/20 text-red-400', icon: Ban },
      pending: { ar: 'معلق', en: 'Pending', color: 'bg-amber-500/20 text-amber-400', icon: AlertTriangle },
    };
    return statuses[status] || statuses.active;
  };

  return (
    <DashboardLayout
      title="أعضاء 606 Fitness"
      titleEn="606 Fitness Members"
      subtitle="إدارة أعضاء ومدربين المنصة"
      subtitleEn="Manage platform members and trainers"
      screenCode="FIT-001"
      backPath="/dashboard/606-fitness"
      onBack={() => navigate('/dashboard/606-fitness')}
      breadcrumb={[
        { labelAr: '606 FITNESS', labelEn: '606 FITNESS', path: '/dashboard/606-fitness' },
        { labelAr: 'الأعضاء', labelEn: 'Members' }
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
            <input
              type="text"
              placeholder={language === 'ar' ? 'البحث بالاسم أو البريد أو الهاتف...' : 'Search by name, email or phone...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/[0.02] border border-white/10 rounded-xl py-3 px-10 text-white placeholder-[#71717A] focus:outline-none focus:border-emerald-500/50 transition-colors"
              data-testid="search-input"
            />
          </div>

          {/* Role Filter */}
          <div className="flex gap-2">
            {[
              { value: 'all', labelAr: 'الكل', labelEn: 'All' },
              { value: 'member', labelAr: 'أعضاء', labelEn: 'Members' },
              { value: 'trainer', labelAr: 'مدربين', labelEn: 'Trainers' },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => { setRoleFilter(filter.value); setCurrentPage(1); }}
                className={`px-4 py-2 rounded-xl border transition-all ${
                  roleFilter === filter.value
                    ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                    : 'bg-white/[0.02] border-white/10 text-[#A1A1AA] hover:border-white/20'
                }`}
                data-testid={`filter-${filter.value}`}
              >
                {language === 'ar' ? filter.labelAr : filter.labelEn}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            onClick={fetchMembers}
            disabled={loading}
            className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Error Message - Show only if no members */}
        {error && members.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3"
          >
            <XCircle className="h-5 w-5 text-red-400" />
            <span className="text-red-400 text-sm">{error}</span>
          </motion.div>
        )}

        {/* Members Table */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-white/[0.02] border-b border-white/10 text-sm font-medium text-[#71717A]">
            <div className="col-span-3">{language === 'ar' ? 'العضو' : 'Member'}</div>
            <div className="col-span-3">{language === 'ar' ? 'معلومات التواصل' : 'Contact'}</div>
            <div className="col-span-2">{language === 'ar' ? 'النوع' : 'Role'}</div>
            <div className="col-span-2">{language === 'ar' ? 'الحالة' : 'Status'}</div>
            <div className="col-span-2 text-center">{language === 'ar' ? 'الإجراءات' : 'Actions'}</div>
          </div>

          {/* Table Body */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-20">
              <Users className="h-12 w-12 mx-auto mb-4 text-[#71717A] opacity-50" />
              <p className="text-[#71717A]">{language === 'ar' ? 'لا يوجد أعضاء' : 'No members found'}</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {members.map((member) => {
                const roleInfo = getRoleLabel(member.role);
                const statusInfo = getStatusLabel(member.status);
                const StatusIcon = statusInfo.icon;

                return (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-white/[0.02] transition-colors"
                  >
                    {/* Member Info */}
                    <div className="col-span-3 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
                        {member.name?.charAt(0) || <User className="h-5 w-5" />}
                      </div>
                      <div>
                        <p className="text-white font-medium">{member.name}</p>
                        <p className="text-[#71717A] text-xs flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {member.joinDate || member.createdAt || '-'}
                        </p>
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="col-span-3 space-y-1">
                      <p className="text-[#A1A1AA] text-sm flex items-center gap-2">
                        <Mail className="h-4 w-4 text-[#71717A]" />
                        {member.email}
                      </p>
                      <p className="text-[#A1A1AA] text-sm flex items-center gap-2">
                        <Phone className="h-4 w-4 text-[#71717A]" />
                        {member.phone || '-'}
                      </p>
                    </div>

                    {/* Role */}
                    <div className="col-span-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium ${roleInfo.color}`}>
                        {language === 'ar' ? roleInfo.ar : roleInfo.en}
                      </span>
                    </div>

                    {/* Status */}
                    <div className="col-span-2">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium ${statusInfo.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {language === 'ar' ? statusInfo.ar : statusInfo.en}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="col-span-2 flex items-center justify-center gap-2">
                      {/* Suspend/Activate */}
                      <button
                        onClick={() => setActionModal({ open: true, type: 'suspend', member })}
                        className={`p-2 rounded-lg transition-colors ${
                          member.status === 'suspended'
                            ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                            : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                        }`}
                        title={member.status === 'suspended' 
                          ? (language === 'ar' ? 'تفعيل الحساب' : 'Activate') 
                          : (language === 'ar' ? 'إيقاف الحساب' : 'Suspend')}
                        data-testid={`suspend-${member.id}`}
                      >
                        {member.status === 'suspended' ? <CheckCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                      </button>

                      {/* Archive (only when suspended) */}
                      <button
                        onClick={() => member.status === 'suspended' && setActionModal({ open: true, type: 'archive', member })}
                        disabled={member.status !== 'suspended'}
                        className={`p-2 rounded-lg transition-colors ${
                          member.status === 'suspended'
                            ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                            : 'bg-white/[0.02] text-[#606060] cursor-not-allowed opacity-50'
                        }`}
                        title={member.status === 'suspended'
                          ? (language === 'ar' ? 'أرشفة' : 'Archive')
                          : (language === 'ar' ? 'يجب إيقاف العضو أولاً' : 'Suspend first')}
                        data-testid={`archive-${member.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg bg-white/[0.02] border border-white/10 text-[#A1A1AA] hover:bg-white/[0.05] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <span className="text-[#A1A1AA] text-sm">
              {language === 'ar' ? `صفحة ${currentPage} من ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg bg-white/[0.02] border border-white/10 text-[#A1A1AA] hover:bg-white/[0.05] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          </div>
        )}
      </section>

      {/* Action Confirmation Modal */}
      <AnimatePresence>
        {actionModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !actionLoading && setActionModal({ open: false, type: null, member: null })}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-md w-full"
            >
              {actionModal.type === 'archive' ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Archive className="h-8 w-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white text-center mb-2">
                    {language === 'ar' ? 'أرشفة العضو' : 'Archive Member'}
                  </h3>
                  <p className="text-[#A1A1AA] text-center mb-6">
                    {language === 'ar'
                      ? `سيتم نقل "${actionModal.member?.name}" إلى الأرشيف. يمكنك استعادته أو حذفه نهائياً لاحقاً من صفحة الأرشيف.`
                      : `"${actionModal.member?.name}" will be moved to the Archive page. You can restore or permanently delete later.`}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setActionModal({ open: false, type: null, member: null })}
                      disabled={actionLoading}
                      className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10 transition-colors disabled:opacity-50"
                    >
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button
                      onClick={() => handleArchiveMember(actionModal.member)}
                      disabled={actionLoading}
                      className="flex-1 py-3 rounded-xl bg-amber-500 text-black font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="confirm-archive-btn"
                    >
                      {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {language === 'ar' ? 'أرشفة' : 'Archive'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                    actionModal.member?.status === 'suspended' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                  }`}>
                    {actionModal.member?.status === 'suspended' 
                      ? <CheckCircle className="h-8 w-8 text-emerald-400" />
                      : <Ban className="h-8 w-8 text-amber-400" />}
                  </div>
                  <h3 className="text-xl font-bold text-white text-center mb-2">
                    {actionModal.member?.status === 'suspended'
                      ? (language === 'ar' ? 'تفعيل الحساب' : 'Activate Account')
                      : (language === 'ar' ? 'إيقاف الحساب' : 'Suspend Account')}
                  </h3>
                  <p className="text-[#A1A1AA] text-center mb-6">
                    {actionModal.member?.status === 'suspended'
                      ? (language === 'ar' 
                          ? `هل تريد تفعيل حساب "${actionModal.member?.name}"؟`
                          : `Do you want to activate "${actionModal.member?.name}"'s account?`)
                      : (language === 'ar' 
                          ? `هل تريد إيقاف حساب "${actionModal.member?.name}"؟ لن يتمكن من الوصول للمنصة.`
                          : `Do you want to suspend "${actionModal.member?.name}"'s account? They won't be able to access the platform.`)}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setActionModal({ open: false, type: null, member: null })}
                      disabled={actionLoading}
                      className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10 transition-colors disabled:opacity-50"
                    >
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button
                      onClick={() => handleToggleSuspend(actionModal.member?.id, actionModal.member?.status)}
                      disabled={actionLoading}
                      className={`flex-1 py-3 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                        actionModal.member?.status === 'suspended'
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                          : 'bg-amber-500 text-black hover:bg-amber-600'
                      }`}
                    >
                      {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {actionModal.member?.status === 'suspended'
                        ? (language === 'ar' ? 'تفعيل' : 'Activate')
                        : (language === 'ar' ? 'إيقاف' : 'Suspend')}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
